import { ref, Ref, onMounted, computed } from "vue";
import { Event } from "@/business/events/domain/events";
import { getActualEvent } from "@/business/events/application/getActualEvent";
import { createEvent } from "@/business/events/application/createEvent";
import { updateEvent } from "@/business/events/application/updateEvent";
import { deleteEvent } from "@/business/events/application/deleteEvent";
import { exportAllGuestsExcel } from "@/business/events/application/exportAllGuestsExcel";
import { Guest } from "@/business/events/domain/events";
import { getAllGuests } from "@/business/events/application/getAllGuests";
import { checkInGuest } from "@/business/events/application/checkInGuest";

export function eventsUse() {
  const modalInfoEvent: Ref<boolean> = ref(false);
  const event: Ref<Event | null> = ref(null);
  const route: Ref<string> = ref("");
  const guests: Ref<Guest[]> = ref([]);
  const query: Ref<string> = ref("");
  const stateFilter: Ref<number> = ref(0);

  onMounted(async () => {
    await getEventFromApi();
    await getGuestsFromApi();
    route.value = await exportAllGuestsExcel.execute();
  });

  async function getEventFromApi(): Promise<void> {
    event.value = await getActualEvent.execute();
  }

  async function getGuestsFromApi(): Promise<void> {
    guests.value = await getAllGuests.execute();
  }

  function formatToDDMMYYYY(date: string): string {
    return date.replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$3/$2/$1");
  }

  function createNewEvent() {
    modalInfoEvent.value = true;
  }

  function editEvent() {
    modalInfoEvent.value = true;
  }

  function closeInformationEventModal() {
    modalInfoEvent.value = false;
  }

  async function createEventOperation(
    name: string,
    lastDate: string,
    maxInvites: string
  ) {
    await createEvent.execute(
      name,
      formatToDDMMYYYY(lastDate),
      Number(maxInvites)
    );
    modalInfoEvent.value = false;
    await getEventFromApi();
  }

  async function editEventOperation(
    name: string,
    lastDate: string,
    maxInvites: string
  ) {
    await updateEvent.execute(
      name,
      formatToDDMMYYYY(lastDate),
      Number(maxInvites)
    );
    modalInfoEvent.value = false;
    await getEventFromApi();
  }

  async function deleteEventOperation() {
    await deleteEvent.execute();
    event.value = null;
    guests.value = [];
  }

  function filterQuery(actualGuestsInfo: Guest[]): Guest[] {
    const normalizeQuery = query.value.toLowerCase();

    return actualGuestsInfo.filter(
      (guest) =>
        guest.clientCode?.toLowerCase().includes(normalizeQuery) ||
        guest.clientName?.toLowerCase().includes(normalizeQuery) ||
        guest.document.toLowerCase().includes(normalizeQuery) ||
        guest.guestName?.toLowerCase().includes(normalizeQuery)
    );
  }

  const filteredGuests = computed(() => {
    let result = guests.value;

    result = changeFilterState(stateFilter.value);

    if (query.value.length > 0) {
      result = filterQuery(result);
    }

    return result;
  });

  function changeFilterState(newState: number): Guest[] {
    if (newState >= 0 && newState <= 2) {
      stateFilter.value = newState;
    }

    let result: Guest[] = guests.value;

    if (newState == 0) result = guests.value;

    if (newState == 1) {
      result = result.filter((item) => item.status == "Pendiente");
    }

    if (newState == 2) {
      result = result.filter((item) => item.status == "Registrado");
    }

    if (query.value.length > 0) {
      result = filterQuery(result);
    }

    return result;
  }

  async function checkGuestOperation(guest: Guest): Promise<void> {
    await checkInGuest.execute(
      guest.clientCode ?? "",
      guest.documentType,
      guest.document
    );
    await getGuestsFromApi();
  }

  function isRegistered(guest: Guest): boolean {
    return guest.status === "Registrado";
  }

  return {
    modalInfoEvent,
    createEventOperation,
    createNewEvent,
    editEvent,
    event,
    closeInformationEventModal,
    editEventOperation,
    deleteEventOperation,
    guests,
    query,
    filteredGuests,
    checkGuestOperation,
    isRegistered,
    changeFilterState,
    route,
  };
}
